
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { collectErrors, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  ATTRIBUTE_SOURCE_ATTRIBUTE,
  ATTRIBUTE_SOURCE_NAME_ID,
  CANONICAL_FIELD_COMMUNITY_REFERENCE,
  COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING,
  CANONICAL_FIELD_EMAIL,
  CANONICAL_FIELD_EXTERNAL_SUBJECT,
  CANONICAL_FIELD_FIRST_NAME,
  CANONICAL_FIELD_LAST_NAME,
  IApiFieldError,
  identityIntegrationsService,
  IDENTITY_INTEGRATION_STATUS_ACTIVE,
  IIdentityAttributeMapping,
  IIdentityIntegration,
} from '@/services/identityIntegrationsService';

const OPTIONAL_FIELDS = [
  CANONICAL_FIELD_EMAIL,
  CANONICAL_FIELD_FIRST_NAME,
  CANONICAL_FIELD_LAST_NAME,
  CANONICAL_FIELD_COMMUNITY_REFERENCE,
];

/**
 * The mapping boundary, as a form: canonical Smart Point fields on the left,
 * whatever this IdP calls them on the right. No field here is named after a
 * provider — that is exactly what the right-hand column is for.
 */
@Component({ name: 'AttributeMappingCard' })
export default class AttributeMappingCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected rows: { [field: string]: string | null } = {};
  protected externalSubjectSource: string = ATTRIBUTE_SOURCE_NAME_ID;
  protected busy: boolean = false;
  protected confirmingExternalSubject: boolean = false;
  protected errors: IApiFieldError[] = [];

  protected readonly optionalFields = OPTIONAL_FIELDS;
  protected readonly sourceNameId = ATTRIBUTE_SOURCE_NAME_ID;

  created() {
    this.reset();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.reset();
  }

  get isAttributeMapped(): boolean {
    return this.integration.community_resolution.mode === COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING;
  }

  /**
   * Receded, never hidden and never locked.
   *
   * Switching resolution to attribute mapping requires this field to already
   * be mapped, so hiding it under `fixed` — or making it read-only — left no
   * way in at all: the field could not be filled because the mode was fixed,
   * and the mode could not be changed because the field was empty. It has to
   * stay fillable ahead of the switch, which is the order an operator actually
   * works in.
   */
  isUnused(field: string): boolean {
    return field === CANONICAL_FIELD_COMMUNITY_REFERENCE && !this.isAttributeMapped;
  }

  /**
   * Attribute-mapped resolution cannot resolve anything without the reference,
   * so an empty one is always worth flagging.
   */
  get missingCommunityReference(): boolean {
    return this.isAttributeMapped && !this.trimmed(CANONICAL_FIELD_COMMUNITY_REFERENCE);
  }

  /**
   * Mirrors the backend rule exactly: an ACTIVE integration may not be left in
   * a state that fails every login, an inactive one may be edited freely and is
   * re-checked on enable. Blocking the button here only saves a round trip to a
   * rejection the server was always going to issue — it is not a second,
   * independent rule.
   */
  get blocksSave(): boolean {
    return this.missingCommunityReference && this.integration.status === IDENTITY_INTEGRATION_STATUS_ACTIVE;
  }

  get sourceItems() {
    return [
      { text: this.$t('identityIntegrations.attributeMapping.sources.name_id'), value: ATTRIBUTE_SOURCE_NAME_ID },
      { text: this.$t('identityIntegrations.attributeMapping.sources.attribute'), value: ATTRIBUTE_SOURCE_ATTRIBUTE },
    ];
  }

  /**
   * Changing this rewrites the lookup key of every existing link, so warn as
   * soon as the form diverges — not only once the save has been rejected.
   */
  get showExternalSubjectWarning(): boolean {
    return this.integration.linked_identity_count > 0 && this.externalSubjectChanged;
  }

  /**
   * Null whenever the subject comes from the NameID, whatever the form still
   * holds. A NameID mapping has no attribute name by definition, so a value
   * left over from a previous choice must not count as configuration — it
   * would otherwise mark the form dirty and fire the "you are changing the
   * external subject" warning when nothing actually changed.
   */
  get externalSubjectName(): string | null {
    return this.externalSubjectSource === ATTRIBUTE_SOURCE_NAME_ID
      ? null
      : this.trimmed(CANONICAL_FIELD_EXTERNAL_SUBJECT);
  }

  get externalSubjectChanged(): boolean {
    const current = this.mappingFor(CANONICAL_FIELD_EXTERNAL_SUBJECT);
    const currentSource = current ? current.source_type : ATTRIBUTE_SOURCE_NAME_ID;
    const currentName = current && current.source_type === ATTRIBUTE_SOURCE_ATTRIBUTE ? current.source_name : null;

    return currentSource !== this.externalSubjectSource || currentName !== this.externalSubjectName;
  }

  get dirty(): boolean {
    if (this.externalSubjectChanged) {
      return true;
    }

    return OPTIONAL_FIELDS.some((field) => {
      const current = this.mappingFor(field);
      return (current ? current.source_name : null) !== this.trimmed(field);
    });
  }

  get generalError(): string | null {
    return generalErrorMessage(this, this.errors, ['attributes'].concat(this.errorRefs()));
  }

  fieldError(field: string): string[] {
    const index = this.payload().findIndex((mapping) => mapping.canonical_field === field);

    if (index === -1) {
      return [];
    }

    return fieldErrorMessages(this, this.errors, 'attributes.' + index + '.source_name').concat(
      fieldErrorMessages(this, this.errors, 'attributes.' + index + '.canonical_field')
    );
  }

  reset() {
    const external = this.mappingFor(CANONICAL_FIELD_EXTERNAL_SUBJECT);

    this.externalSubjectSource = external ? external.source_type : ATTRIBUTE_SOURCE_NAME_ID;
    this.rows = { [CANONICAL_FIELD_EXTERNAL_SUBJECT]: external ? external.source_name : null };

    OPTIONAL_FIELDS.forEach((field) => {
      const mapping = this.mappingFor(field);
      this.$set(this.rows, field, mapping ? mapping.source_name : null);
    });

    this.errors = [];
    this.confirmingExternalSubject = false;
  }

  async submit(confirmed: boolean = false) {
    if (!confirmed && this.showExternalSubjectWarning) {
      this.confirmingExternalSubject = true;
      return;
    }

    this.busy = true;
    this.errors = [];
    try {
      const updated = await identityIntegrationsService.replaceAttributeMappings(
        this.integration.id,
        this.payload(),
        confirmed
      );
      this.confirmingExternalSubject = false;
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.confirmingExternalSubject = false;
      this.errors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }

  /**
   * An unmapped field is an absent row, never a row with an empty name: the
   * backend treats "no mapping" as a valid fail-closed state and an empty
   * attribute name as a configuration error.
   */
  private payload(): IIdentityAttributeMapping[] {
    const mappings: IIdentityAttributeMapping[] = [];
    const externalSubjectName = this.externalSubjectName;

    if (this.externalSubjectSource === ATTRIBUTE_SOURCE_NAME_ID) {
      mappings.push({
        canonical_field: CANONICAL_FIELD_EXTERNAL_SUBJECT,
        source_type: ATTRIBUTE_SOURCE_NAME_ID,
        source_name: null,
        required: true,
      });
    } else if (externalSubjectName) {
      mappings.push({
        canonical_field: CANONICAL_FIELD_EXTERNAL_SUBJECT,
        source_type: ATTRIBUTE_SOURCE_ATTRIBUTE,
        source_name: externalSubjectName,
        required: true,
      });
    }

    OPTIONAL_FIELDS.forEach((field) => {
      const name = this.trimmed(field);

      if (name) {
        mappings.push({
          canonical_field: field,
          source_type: ATTRIBUTE_SOURCE_ATTRIBUTE,
          source_name: name,
          required: false,
        });
      }
    });

    return mappings;
  }

  private errorRefs(): string[] {
    return this.payload().map((mapping, index) => 'attributes.' + index + '.source_name');
  }

  private trimmed(field: string): string | null {
    const value = (this.rows[field] || '').trim();
    return value === '' ? null : value;
  }

  private mappingFor(field: string): IIdentityAttributeMapping | null {
    const attributes = this.integration.identity_mapping.attributes || [];
    const found = attributes.filter((mapping) => mapping.canonical_field === field);

    return found.length ? found[0] : null;
  }
}
